import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut, RefreshCw, Search, Trash2, ChevronRight, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    adminLogin, fetchLeads, fetchLeadStats, updateLeadStatus, deleteLead,
    verifyAdmin, setAdminToken, getAdminToken,
} from "@/lib/api";
import { PULSE_TESTIDS } from "@/lib/pulseConstants";
import PulseWordmark from "@/components/shared/PulseWordmark";

const STATUS_OPTIONS = [
    { value: "all", label: "All" },
    { value: "new", label: "New" },
    { value: "contacted", label: "Contacted" },
    { value: "qualified", label: "Qualified" },
    { value: "won", label: "Won" },
    { value: "lost", label: "Lost" },
];

function badgeVariant(status) {
    switch (status) {
        case "new": return "bg-[#FF2A2A]/15 text-[#FF6B6B] border-[#FF2A2A]/40";
        case "contacted": return "bg-blue-500/15 text-blue-300 border-blue-500/40";
        case "qualified": return "bg-purple-500/15 text-purple-300 border-purple-500/40";
        case "won": return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
        case "lost": return "bg-white/10 text-white/60 border-white/20";
        default: return "bg-white/10 text-white/70 border-white/20";
    }
}

export default function AdminPage() {
    const [authed, setAuthed] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        document.title = "Pulse Admin";
        // Keep this route out of public search indexes by overriding the site-wide
        // <meta name="robots"> tag while mounted, and restoring it on unmount.
        const meta = document.querySelector('meta[name="robots"]');
        const previous = meta ? meta.getAttribute("content") : null;
        const created = !meta;
        const target = meta || document.createElement("meta");
        if (created) target.setAttribute("name", "robots");
        target.setAttribute("content", "noindex, nofollow, noarchive, nosnippet");
        if (created) document.head.appendChild(target);

        (async () => {
            const token = getAdminToken();
            if (!token) { setChecking(false); return; }
            try { await verifyAdmin(); setAuthed(true); }
            catch { setAdminToken(null); setAuthed(false); }
            finally { setChecking(false); }
        })();

        return () => {
            if (created) {
                if (target.parentNode) target.parentNode.removeChild(target);
            } else if (previous !== null) {
                target.setAttribute("content", previous);
            }
        };
    }, []);

    if (checking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#07070A] text-white">
                <div className="font-mono text-sm text-white/60">Checking session...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#07070A] text-white" data-testid={PULSE_TESTIDS.admin.page}>
            {!authed ? (
                <AdminLogin onSuccess={() => setAuthed(true)} />
            ) : (
                <AdminDashboard onLogout={() => { setAdminToken(null); setAuthed(false); }} />
            )}
        </div>
    );
}

function AdminLogin({ onSuccess }) {
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    async function onSubmit(e) {
        e.preventDefault();
        if (!password) return;
        try {
            setSubmitting(true);
            const data = await adminLogin(password);
            setAdminToken(data.token);
            toast.success("Welcome back.");
            onSuccess();
        } catch (err) {
            const detail = err?.response?.data?.detail || "Incorrect password.";
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="min-h-screen relative flex items-center justify-center px-4">
            <div className="absolute inset-0 hero-radial pointer-events-none" />
            <div className="absolute top-6 left-6">
                <Link to="/" aria-label="Back to REFORM Pulse homepage">
                    <PulseWordmark size="sm" showTagline />
                </Link>
            </div>
            <form onSubmit={onSubmit} className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0B0C10] p-8">
                <div className="font-mono text-xs text-[#FF2A2A] tracking-[0.24em] uppercase mb-3">Admin</div>
                <h1 className="font-display text-4xl mb-6 uppercase">Pulse Control Room</h1>
                <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-white/70 font-mono">Password</Label>
                    <Input
                        type="password"
                        data-testid={PULSE_TESTIDS.admin.passwordInput}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter admin password"
                        className="bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#FF2A2A]"
                        autoFocus
                    />
                </div>
                <Button
                    type="submit"
                    disabled={submitting}
                    data-testid={PULSE_TESTIDS.admin.submit}
                    className="cta-glow w-full mt-6 bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-12"
                >
                    {submitting ? "Signing in..." : "Enter Control Room"}
                </Button>
                <p className="text-xs text-white/40 mt-4 font-mono uppercase tracking-widest text-center">
                    Restricted – REFORM Pulse team only
                </p>
            </form>
        </div>
    );
}

function AdminDashboard({ onLogout }) {
    const [leads, setLeads] = useState([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState(null);
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (status !== "all") params.status = status;
            if (q) params.q = q;
            const [leadsResp, statsResp] = await Promise.all([
                fetchLeads(params),
                fetchLeadStats(),
            ]);
            setLeads(leadsResp.items || []);
            setTotal(leadsResp.total || 0);
            setStats(statsResp);
        } catch (err) {
            toast.error("Failed to load leads. Try re-login.");
            if (err?.response?.status === 401) onLogout();
        } finally {
            setLoading(false);
        }
    }, [status, q, onLogout]);

    useEffect(() => { load(); }, [load]);

    async function onChangeStatus(id, newStatus) {
        try {
            await updateLeadStatus(id, newStatus);
            toast.success(`Marked as ${newStatus}.`);
            load();
            setSelected((s) => (s && s.id === id ? { ...s, status: newStatus } : s));
        } catch (err) {
            toast.error("Failed to update status.");
        }
    }

    async function onDelete(id) {
        if (!confirm("Delete this lead permanently?")) return;
        try {
            await deleteLead(id);
            toast.success("Lead deleted.");
            setSelected(null);
            load();
        } catch { toast.error("Failed to delete lead."); }
    }

    function exportCsv() {
        if (leads.length === 0) { toast.info("No leads to export."); return; }
        const headers = ["created_at", "name", "company", "email", "phone", "brand", "event_type", "event_date", "budget", "goal", "status", "source_page", "message"];
        const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const rows = leads.map((l) => headers.map((h) => escape(l[h])).join(","));
        const csv = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `pulse-leads-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    }

    return (
        <div>
            {/* Top bar */}
            <div className="sticky top-0 z-40 border-b border-white/10 bg-black/85 backdrop-blur">
                <div className="container-editorial h-16 flex items-center justify-between">
                    <Link to="/" aria-label="Back to REFORM Pulse homepage">
                        <PulseWordmark size="sm" showTagline={false} suffix="Admin" />
                    </Link>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={load} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5 h-10 px-4">
                            <RefreshCw size={14} className="mr-2" /> Refresh
                        </Button>
                        <Button variant="outline" onClick={exportCsv} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5 h-10 px-4" data-testid="admin-export-csv">
                            Export CSV
                        </Button>
                        <Button variant="outline" onClick={onLogout} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5 h-10 px-4" data-testid={PULSE_TESTIDS.admin.logout}>
                            <LogOut size={14} className="mr-2" /> Logout
                        </Button>
                    </div>
                </div>
            </div>

            <div className="container-editorial py-10">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-8">
                    <StatCard label="Total Briefs" value={stats?.total ?? 0} accent />
                    <StatCard label="New" value={stats?.by_status?.new ?? 0} />
                    <StatCard label="Contacted" value={stats?.by_status?.contacted ?? 0} />
                    <StatCard label="Won" value={stats?.by_status?.won ?? 0} />
                    <StatCard label="Lost" value={stats?.by_status?.lost ?? 0} />
                </div>

                <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search name, email, brand, message..."
                            className="pl-10 bg-black/40 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#FF2A2A]"
                            data-testid="admin-search-input"
                        />
                    </div>
                    <div className="w-full md:w-56">
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger className="bg-black/40 border-white/15 text-white focus:ring-[#FF2A2A]" data-testid="admin-status-filter">
                                <SelectValue placeholder="Filter by status" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0B0C10] border-white/10 text-white">
                                {STATUS_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="text-xs font-mono text-white/50 uppercase tracking-widest">
                        {total} total
                    </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-[#0B0C10] overflow-hidden">
                    <Table data-testid={PULSE_TESTIDS.admin.table}>
                        <TableHeader>
                            <TableRow className="border-white/10 hover:bg-transparent">
                                <TableHead className="text-white/50 font-mono text-[11px] uppercase tracking-widest">Received</TableHead>
                                <TableHead className="text-white/50 font-mono text-[11px] uppercase tracking-widest">Contact</TableHead>
                                <TableHead className="text-white/50 font-mono text-[11px] uppercase tracking-widest">Brand</TableHead>
                                <TableHead className="text-white/50 font-mono text-[11px] uppercase tracking-widest">Event</TableHead>
                                <TableHead className="text-white/50 font-mono text-[11px] uppercase tracking-widest">Budget</TableHead>
                                <TableHead className="text-white/50 font-mono text-[11px] uppercase tracking-widest">Status</TableHead>
                                <TableHead className="text-white/50 font-mono text-[11px] uppercase tracking-widest text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow className="border-white/10">
                                    <TableCell colSpan={7} className="text-center py-10 text-white/50 font-mono text-sm">Loading briefs...</TableCell>
                                </TableRow>
                            )}
                            {!loading && leads.length === 0 && (
                                <TableRow className="border-white/10">
                                    <TableCell colSpan={7} className="text-center py-14 text-white/50">
                                        No briefs yet. As they land, they'll show up here.
                                    </TableCell>
                                </TableRow>
                            )}
                            {leads.map((l) => (
                                <TableRow key={l.id} data-testid={PULSE_TESTIDS.admin.row} className="border-white/10 hover:bg-white/[0.03]">
                                    <TableCell className="align-top">
                                        <div className="text-sm text-white/85">{formatDateShort(l.created_at)}</div>
                                        <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{l.source_page || "home"}</div>
                                    </TableCell>
                                    <TableCell className="align-top">
                                        <div className="text-sm text-white font-medium">{l.name}</div>
                                        <div className="text-xs text-white/60">{l.email}</div>
                                        <div className="text-xs text-white/40">{l.company}</div>
                                    </TableCell>
                                    <TableCell className="align-top text-sm text-white/85">{l.brand}</TableCell>
                                    <TableCell className="align-top">
                                        <div className="text-sm text-white/85">{l.event_type}</div>
                                        <div className="text-xs text-white/50">{l.event_date || "—"}</div>
                                    </TableCell>
                                    <TableCell className="align-top text-sm text-white/85">{l.budget}</TableCell>
                                    <TableCell className="align-top">
                                        <Badge className={`border font-mono text-[10px] uppercase tracking-widest ${badgeVariant(l.status)}`}>
                                            {l.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="align-top text-right">
                                        <Button size="sm" variant="ghost" onClick={() => setSelected(l)} className="text-white/80 hover:text-white hover:bg-white/5 rounded-full">
                                            View <ChevronRight size={14} className="ml-1" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="bg-[#0B0C10] border border-white/10 text-white max-w-2xl">
                    {selected && (
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
                                Pulse Brief – {formatDate(selected.created_at)}
                            </div>
                            <DialogTitle asChild>
                                <h3 className="font-display text-3xl text-white mb-1 uppercase">{selected.brand}</h3>
                            </DialogTitle>
                            <DialogDescription asChild>
                                <p className="text-sm text-white/60">Submitted from {selected.source_page}</p>
                            </DialogDescription>

                            <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
                                <KV label="Contact" value={selected.name} />
                                <KV label="Company" value={selected.company} />
                                <KV label="Email" value={<a href={`mailto:${selected.email}`} className="underline decoration-white/30 hover:text-[#FF2A2A]">{selected.email}</a>} />
                                <KV label="Phone / WhatsApp" value={selected.phone} />
                                <KV label="Event Type" value={selected.event_type} />
                                <KV label="Estimated Date" value={selected.event_date || "—"} />
                                <KV label="Budget" value={selected.budget} />
                                <KV label="Goal" value={selected.goal} />
                            </div>

                            <div className="mt-6">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Brief</div>
                                <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap rounded-xl border border-white/10 bg-black/40 p-4">{selected.message}</p>
                            </div>

                            <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-6 border-t border-white/10">
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">Status</span>
                                    <Select value={selected.status} onValueChange={(v) => onChangeStatus(selected.id, v)}>
                                        <SelectTrigger className="bg-black/40 border-white/15 text-white h-10 w-44">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0B0C10] border-white/10 text-white">
                                            {STATUS_OPTIONS.filter((o) => o.value !== "all").map((o) => (
                                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button asChild variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5 h-10 px-4">
                                        <a href={`mailto:${selected.email}?subject=RE:%20${encodeURIComponent(selected.brand)}%20Pulse%20Brief`}>
                                            <ExternalLink size={14} className="mr-2" /> Email back
                                        </a>
                                    </Button>
                                    <Button variant="outline" onClick={() => onDelete(selected.id)} className="rounded-full border-white/20 bg-transparent text-white hover:bg-red-500/10 hover:text-red-300 h-10 px-4">
                                        <Trash2 size={14} className="mr-2" /> Delete
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function StatCard({ label, value, accent }) {
    return (
        <div className={`rounded-2xl border p-5 ${accent ? "border-[#FF2A2A]/40 bg-[#FF2A2A]/5" : "border-white/10 bg-[#0B0C10]"}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">{label}</div>
            <div className="font-display text-4xl text-white leading-none">{value}</div>
        </div>
    );
}

function KV({ label, value }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</div>
            <div className="text-white">{value}</div>
        </div>
    );
}

function formatDate(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}
function formatDateShort(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    } catch { return iso; }
}
